import { useState, useEffect } from "react";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { AlertTriangle, CheckCircle, Clock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;

export default function DemoAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    try {
      const cached = localStorage.getItem("dash_alerts");
      if (cached) { setData(JSON.parse(cached)); setLoading(false); }
    } catch {}
    axios.get(`${API}/alerts`).then(r => {
      setData(r.data);
      try { localStorage.setItem("dash_alerts", JSON.stringify(r.data)); } catch {}
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return <DemoLayout><div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm" style={{ color: "var(--text-muted)" }}>Loading Alerts...</div></div></DemoLayout>;
  }

  const active = (data.active || []).filter(a => !dismissed.has(a.id));

  return (
    <DemoLayout alertCount={active.length}>
      <div className="space-y-5" data-testid="alerts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Alerts & Actions</h1>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
              {active.length} active alert{active.length !== 1 ? "s" : ""} &mdash; {data.counts?.critical || 0} critical, {data.counts?.warning || 0} warning
            </p>
          </div>
          {active.length > 0 && (
            <button
              className="text-[11px] px-3 py-1.5 rounded-md transition-colors"
              style={{ background: "var(--surface-muted)", color: "var(--text-secondary)" }}
              onClick={() => setDismissed(new Set(data.active.map(a => a.id)))}
              data-testid="dismiss-all"
            >
              Dismiss all
            </button>
          )}
        </div>

        {/* Active Alerts */}
        <div className="space-y-3" data-testid="active-alerts">
          {active.length === 0 ? (
            <div className="card-m p-8 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--color-success)" }} />
              <div className="text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>All clear</div>
              <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>No active alerts right now</div>
            </div>
          ) : (
            active.map((alert) => {
              const isC = alert.type === "critical";
              const borderColor = isC ? "var(--color-danger)" : "var(--color-warning)";
              return (
                <div key={alert.id} className="card-m p-5" style={{ borderLeft: `3px solid ${borderColor}` }} data-testid={`alert-${alert.id}`}>
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: borderColor }} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`badge-m text-[10px] ${isC ? "badge-danger" : "badge-warning"}`}>{isC ? "CRITICAL" : "WARNING"}</span>
                        <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>March 19, 2026</span>
                      </div>
                      <div className="text-[14px] font-medium mb-1" style={{ color: "var(--text-primary)" }}>{alert.title}</div>
                      <div className="text-[12px] mb-2" style={{ color: "var(--text-secondary)" }}>{alert.description}</div>
                      {alert.recommended_action && (
                        <div className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
                          Recommended: {alert.recommended_action}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          className="text-[11px] font-medium px-3 py-1.5 rounded-md transition-colors"
                          style={{ background: "var(--color-accent-bg)", color: "var(--color-accent)" }}
                        >
                          Take action
                        </button>
                        <button
                          className="text-[11px] px-3 py-1.5 rounded-md transition-colors"
                          style={{ background: "var(--surface-muted)", color: "var(--text-secondary)" }}
                          onClick={() => setDismissed(prev => new Set([...prev, alert.id]))}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* History */}
        <div>
          <h2 className="text-sm font-semibold mb-3" style={{ color: "var(--text-primary)" }}>Alert History</h2>
          <div className="space-y-1.5">
            {(data.history || []).map((h, i) => (
              <div key={i} className="flex items-center gap-3 py-2 text-[12px]" style={{ borderBottom: "var(--border-width) solid var(--border-subtle)" }}>
                <span className={`badge-m text-[10px] ${h.status === "actioned" ? "badge-success" : h.status === "dismissed" ? "badge-muted" : "badge-info"}`}>
                  {h.status}
                </span>
                <span style={{ color: "var(--text-secondary)" }}>{h.title}</span>
                <span className="ml-auto" style={{ color: "var(--text-muted)" }}>{h.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}
